/* -------------------------- Design imports start -------------------------- */
import { FormControlLabel, Grid, Switch, TextField, Typography, IconButton, Box } from '@mui/material'
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material'
import { Timeline } from 'antd'
import moment from 'moment'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '../../../components/Button'
import { fetchVersionHistory, fetchVersionComparison } from '../api/versionApi'
import { type Version, type VersionComparison } from '../utils/types'
import CompareVersionDrawer from './CompareVersionDrawer'
import { Stack } from '@mui/material'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  formState: {
    state: any | undefined
    setState: React.Dispatch<React.SetStateAction<any | undefined>>
  }
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function VersionSubmit(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const { formState } = props
  const { t } = useTranslation(['common'])
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [showVersionData, setShowVersionData] = useState<number>(-1)
  const [compareOpen, setCompareOpen] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [versions, setVersions] = useState<Version[]>([])
  const [versionDifferences, setVersionDifferences] = useState<VersionComparison | undefined>(
    undefined
  )
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (formState.state?.self) {
      void (async () => {
        await fetchVersions(formState.state.self)
      })()
    }
  }, [formState.state?.self])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  async function fetchVersions(object_self: string): Promise<void> {
    try {
      const history = await fetchVersionHistory(object_self)
      setVersions(history)
    } catch (error) {
      console.error('Error fetching versions:', error)
    }
  }

  const handleFetchVersionDifferences = async (
    object_self: string,
    version1: string,
    version2: string
  ): Promise<void> => {
    try {
      const comparison = await fetchVersionComparison(object_self, version1, version2)
      setVersionDifferences(comparison)
    } catch (error) {
      console.error('Error fetching version differences:', error)
    }
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const timelineItems = versions.map((version: Version, index: number) => {
    return {
      key: index,
      color: "#16a9e4",
      children: (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ flex: 1 }}>{version.comment}</Typography>
            <IconButton
              id="expandButton"
              aria-label="expand"
              size="small"
              sx={{ marginLeft: 'auto' }}
              onClick={() =>
                showVersionData === index ? setShowVersionData(-1) : setShowVersionData(index)
              }
            >
              {showVersionData === index ? (
                <KeyboardArrowUpOutlined />
              ) : (
                <KeyboardArrowDownOutlined />
              )}
            </IconButton>
          </Box>
          {showVersionData === index && (
            <>
              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                {t('common:content.label.user')}: {version.user} <br />
                {t('common:content.label.date')}:{' '}
                {moment(version.created_date).format('MMMM Do YYYY, h:mm:ss a')}
              </Typography>
              {index !== 0 && formState.state?.self && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Button
                    id="compareButton"
                    variant="contained"
                    size="small"
                    sx={{ maxWidth: '450px' }}
                    onClick={() => {
                      void (async () => {
                        await handleFetchVersionDifferences(
                          formState.state.self,
                          version.version_id,
                          versions[0].version_id
                        )
                      })()
                      setCompareOpen(true)
                    }}
                  >
                    {t('common:interaction.button.compareWithCurrentVersion')}
                  </Button>
                </Stack>
              )}
            </>
          )}
        </>
      )
    }
  })
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  
  // Safely get newVersion and versionComment values
  const newVersion = formState.state?.newVersion ?? false
  const versionComment = formState.state?.versionComment ?? ''
  
  const handleToggleNewVersion = (): void => {
    formState.setState((prev: any) => {
      const currentState = prev ?? {}
      const newState = { 
        ...currentState, 
        newVersion: !currentState.newVersion,
        // Reset versionComment when turning off newVersion
        versionComment: !currentState.newVersion ? currentState.versionComment : ''
      }
      return newState
    })
  }
  
  const handleVersionCommentChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    formState.setState((prev: any) => {
      const currentState = prev ?? {}
      return { 
        ...currentState, 
        versionComment: e.target.value 
      }
    })
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                id="newVersion"
                checked={newVersion}
                onChange={handleToggleNewVersion}
              />
            }
            label={t('content.label.newVersionText')}
          />
        </Grid>
        {newVersion && (
          <Grid item xs={12}>
            <TextField
              id="versionComment"
              fullWidth
              multiline
              rows={4}
              label={t('content.label.versionComment')}
              value={versionComment}
              onChange={handleVersionCommentChange}
            />
          </Grid>
        )}
        {versions.length > 0 && (
          <Grid item xs={12} sx={{ paddingLeft: '30px' }}>
            <Box sx={{ width: '100%', '& .ant-timeline': { width: '100%' } }}>
              <Timeline mode={'left'} items={timelineItems} />
            </Box>
          </Grid>
        )}
      </Grid>
      {formState && (
        <CompareVersionDrawer
          open={compareOpen}
          setOpen={setCompareOpen}
          formState={formState}
          versionDifferences={versionDifferences}
          onConfirm={() => null}
        />
      )}
    </>
  )
}
